import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SUR_LE_WEB, colors, radius } from '../theme/tokens';
import { DEFAULT_ACCENT } from '../theme/templates';
import type { CvTemplate } from '../types';
import { PROPS_LTR } from '../i18n/sens';

export interface VignetteCvProps {
  template: CvTemplate;
  accentColor?: string | null;
  // 'lg' : la feuille du CV principal (72 x 96). 'sm' : la ligne d'une liste (32 x 42).
  taille?: 'lg' | 'sm';
  testID?: string;
}

const DIMENSIONS = {
  lg: { largeur: 72, hauteur: 96, trait: 4, titre: 5, ecart: 3 },
  sm: { largeur: 32, hauteur: 42, trait: 2, titre: 3, ecart: 3 },
} as const;

/**
 * Feuille schématique d'un CV : quelques traits, une bande de couleur pour le modèle coloré. Ce
 * n'est pas un rendu du CV (voir `CvMiniature`, qui coûte un vrai rendu) : c'est ce qu'il faut à
 * une ligne de liste, où l'on reconnaît le modèle et la teinte, pas le contenu.
 *
 * Décorative : masquée au lecteur d'écran, le titre du CV à côté dit tout. Jamais retournée en
 * droite à gauche, comme le document qu'elle représente.
 */
export function VignetteCv({ template, accentColor, taille = 'sm', testID }: VignetteCvProps) {
  const d = DIMENSIONS[taille];
  const teinte = accentColor ?? DEFAULT_ACCENT;
  const trait = { height: d.trait, marginTop: d.ecart };
  const cadre = { width: d.largeur, height: d.hauteur };
  // SUR LE WEB, un rembourrage en pourcentage se calcule sur la largeur du CONTENEUR de la feuille
  // (la ligne de liste, 400 px), pas sur la feuille de 32 px : elle devenait un rectangle vide.
  // Les mêmes proportions sont donc posées en pixels. Sur appareil, rien ne change.
  const pxSobre = SUR_LE_WEB
    ? { paddingHorizontal: Math.round(d.largeur * 0.12), paddingTop: Math.round(d.hauteur * 0.1) }
    : null;
  const pxColonne = SUR_LE_WEB
    ? {
        paddingStart: Math.round(d.largeur * 0.04),
        paddingEnd: Math.round(d.largeur * 0.07),
        paddingTop: Math.round(d.hauteur * 0.12),
      }
    : null;

  if (template === 'accent') {
    return (
      <View
        testID={testID}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={[styles.feuille, cadre]}
        {...PROPS_LTR}
      >
        <View testID={testID ? `${testID}-bande` : undefined} style={[styles.bande, { backgroundColor: teinte }]} />
        <View style={[styles.colonne, pxColonne]}>
          <View style={[styles.ligne, { height: d.trait, width: '80%', backgroundColor: teinte }]} />
          <View style={[styles.ligne, trait, { width: '100%' }]} />
          <View style={[styles.ligne, trait, { width: '86%' }]} />
          <View style={[styles.ligne, trait, { width: '100%' }]} />
          <View style={[styles.ligne, trait, { width: '70%' }]} />
        </View>
      </View>
    );
  }

  return (
    <View
      testID={testID}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[styles.feuille, styles.sobre, cadre, pxSobre]}
      {...PROPS_LTR}
    >
      <View style={[styles.ligne, { height: d.titre, width: '55%', backgroundColor: colors.night }]} />
      <View style={[styles.ligne, trait, { width: '35%' }]} />
      <View style={[styles.filet, { marginTop: d.ecart + 1 }]} />
      <View style={[styles.ligne, trait, { width: '100%' }]} />
      <View style={[styles.ligne, trait, { width: '90%' }]} />
      <View style={[styles.ligne, trait, { width: '96%' }]} />
      <View style={[styles.ligne, trait, { width: '60%' }]} />
    </View>
  );
}

const styles = StyleSheet.create({
  feuille: {
    direction: 'ltr',
    flexDirection: 'row',
    backgroundColor: colors.white,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: colors.grey200,
    overflow: 'hidden',
  },
  sobre: { flexDirection: 'column', paddingHorizontal: '12%', paddingTop: '10%' },
  bande: { width: '32%', height: '100%' },
  colonne: { flex: 1, paddingStart: '6%', paddingEnd: '10%', paddingTop: '12%' },
  ligne: { borderRadius: 2, backgroundColor: colors.grey200 },
  filet: { height: 1, backgroundColor: colors.grey200 },
});
